/**
 * Shared auth + admin scope gate for role-aware React Query hooks.
 * Collapses the repeated useAuth + useAdminClients + isRoleQueryReady boilerplate.
 */

import { useCallback } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import {
  useAdminClients,
  useEffectiveClientId,
  useEffectiveCompanyId
} from '@/contexts/AdminClientsContext';
import { isRoleQueryReady, queryRoleKey } from '@/utils/apiRole';

const ROLE_NOT_READY_MESSAGE = 'Role context not ready for mutation';

/**
 * Auth and admin-scope state shared by role-aware query hooks.
 * Use {@link useRoleMutationGate} when mutations need {@link assertReady} or cache invalidation helpers.
 */
export function useRoleQueryGate() {
  const { payload, loading, isAuthenticated } = useAuth();
  const role = payload?.role;
  const { selectedClientId, selectedCompanyId, isSelectionReady } = useAdminClients();
  const effectiveClientId = useEffectiveClientId();
  const effectiveCompanyId = useEffectiveCompanyId();

  const queryKey = queryRoleKey(role, selectedClientId, selectedCompanyId);
  const ready = isRoleQueryReady(
    role,
    loading,
    isSelectionReady,
    selectedClientId,
    isAuthenticated
  );

  return {
    role,
    payload,
    loading,
    isAuthenticated,
    selectedClientId,
    selectedCompanyId,
    isSelectionReady,
    effectiveClientId,
    effectiveCompanyId,
    ready,
    /** Full role-aware cache key segments, e.g. `['admin', clientId, companyId]` or `['client']`. */
    queryKey,
    /** First segment of {@link queryKey} — `'admin'` or `'client'`. */
    roleKey: queryKey[0]
  };
}

/**
 * Role gate for mutations: includes everything from {@link useRoleQueryGate} plus
 * readiness assertion and query invalidation helpers.
 *
 * @returns Gate state with {@link assertReady} and {@link invalidateWithRoleKey}
 */
export function useRoleMutationGate() {
  const gate = useRoleQueryGate();

  const assertReady = useCallback(() => {
    if (!gate.ready) {
      throw new Error(ROLE_NOT_READY_MESSAGE);
    }
  }, [gate.ready]);

  const invalidateWithRoleKey = useCallback(
    (queryClient: QueryClient, prefix: readonly unknown[]) => {
      void queryClient.invalidateQueries({
        queryKey: [...prefix, ...gate.queryKey]
      });
    },
    [gate.queryKey]
  );

  return {
    ...gate,
    assertReady,
    invalidateWithRoleKey
  };
}
